import { useEffect, useState, type FormEvent } from 'react';
import { enviosCampanaService, type OrigenDisponibleCampana, type RespaldoTransferencia } from '../services/enviosCampanaService';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { formatCurrency } from '../utils/formatters';
import { Button } from './ui/Button';
import { Checkbox } from './ui/Checkbox';
import { DatePicker } from './ui/DatePicker';
import { DialogShell } from './ui/DialogShell';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { TextInput } from './ui/TextInput';
import { Textarea } from './ui/Textarea';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { hoyEnvioChile } from './enviosCampana/accionesEnvio';
import { mensajeDeFalloAlSubir, resolverRespaldoTransferencia } from './enviosCampana/respaldoTransferencia';
import { useOperacionEnvio } from './enviosCampana/useOperacionEnvio';
import { useAuth } from '../context/AuthContext';
import { puedeOperarDinero } from '../utils/permissions';

interface Props {
  readonly campanaMasterId: string;
  readonly diocesisId: string;
  readonly readOnly: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function RemitirCampanaModal({ campanaMasterId, diocesisId, readOnly, onClose, onSuccess }: Props) {
  const { usuario } = useAuth();
  const autorizado = !readOnly && puedeOperarDinero(usuario, { tipo: 'diocesis', id: diocesisId, provinciaEclesiasticaId: null });
  const operacion = useOperacionEnvio();
  const [origenes, setOrigenes] = useState<OrigenDisponibleCampana[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [montos, setMontos] = useState<Readonly<Record<string, string>>>({});
  const [fecha, setFecha] = useState(hoyEnvioChile);
  const [descripcion, setDescripcion] = useState('');
  const [transferido, setTransferido] = useState(false);
  const [errorValidacion, setErrorValidacion] = useState<string | null>(null);
  const [preparando, setPreparando] = useState(false);
  // El comprobante de un envío lo revisa quien lo recibe antes de confirmar: no se objeta ni se sube después.
  const comprobante = useComprobanteFormulario({
    entidad: { tipo: 'diocesis', id: diocesisId, nombre: '' },
    fechaMovimiento: null,
    permiteSubirDespues: false,
    pideMedioPago: true,
    sentidoDinero: 'egreso',
    mensajeFaltante: 'Adjunta el comprobante de la transferencia.',
  });
  const bloqueado = preparando || operacion.procesando || operacion.incierto || operacion.completado;

  useEffect(() => {
    let vigente = true;
    enviosCampanaService.getOrigenesDisponibles(campanaMasterId, diocesisId)
      .then(datos => { if (vigente) setOrigenes(datos.filter(o => ['nacional', 'global'].includes(o.campana_nivel_origen ?? ''))); })
      .catch(() => { if (vigente) setErrorCarga('No pudimos consultar las recepciones disponibles.'); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [campanaMasterId, diocesisId]);

  const asignaciones = origenes.filter(o => montos[o.id] !== undefined)
    .map(o => ({ envioPrimerTramoId: o.id, monto: Number(montos[o.id]) }));
  const total = asignaciones.reduce((s, a) => s + Math.round(a.monto * 100), 0) / 100;
  const registrar = async (e: FormEvent) => {
    e.preventDefault();
    if (!autorizado) { setErrorValidacion('Solo la junta de esta diócesis puede remitir su fondo.'); return; }
    const invalida = asignaciones.some(a => !Number.isFinite(a.monto) || a.monto <= 0
      || a.monto > (origenes.find(o => o.id === a.envioPrimerTramoId)?.remanente ?? 0));
    if (!transferido || asignaciones.length === 0 || invalida) {
      setErrorValidacion('Selecciona recepciones, revisa sus importes y confirma la transferencia ya realizada.');
      return;
    }
    const errorComprobante = comprobante.validar();
    if (errorComprobante) { setErrorValidacion(errorComprobante); return; }
    setErrorValidacion(null);
    // Se resuelve una vez: si el resultado es incierto, el reintento repite la misma operación con el mismo comprobante.
    let respaldo: RespaldoTransferencia;
    setPreparando(true);
    try {
      respaldo = await resolverRespaldoTransferencia(comprobante);
    } catch (error_: unknown) {
      setErrorValidacion(mensajeDeFalloAlSubir(error_));
      return;
    } finally { setPreparando(false); }
    const respuesta = await operacion.ejecutar(solicitudId => {
      const params = { campanaMasterId, diocesisId, fechaEnvio: fecha,
        descripcion: descripcion.trim() || null, asignaciones, solicitudId,
        respaldo };
      return () => enviosCampanaService.crearSegundoTramo(params);
    });
    if (respuesta.estado === 'confirmado') { onClose(); onSuccess(); }
  };

  return <DialogShell isOpen onClose={onClose} title="Registrar remisión a JAN" size="lg"
    loading={bloqueado} onBeforeClose={() => !bloqueado}>
    <form onSubmit={registrar} className="space-y-4">
      <p className="text-sm text-slate-600">Agrupa recepciones confirmadas de esta campaña.
        Registra el dinero ya enviado a la Junta Administrativa Nacional. El saldo mostrado es orientativo;
        la operación verifica la disponibilidad al guardar.</p>
      <FormErrorAlert error={errorCarga ?? errorValidacion ?? operacion.error} />
      <ComprobanteInput comprobante={comprobante} titulo="Comprobante de la transferencia"
        deshabilitado={bloqueado || cargando || !!errorCarga} />
      {cargando && <output className="block">Consultando recepciones…</output>}
      {!cargando && !errorCarga && origenes.length === 0 && <p>No hay recepciones disponibles para remitir.</p>}
      <fieldset disabled={bloqueado || cargando || !!errorCarga} className="space-y-4">
        {origenes.map(origen => <div key={origen.id} className="rounded-xl border border-slate-200 p-4 space-y-3">
          <Checkbox label={`${origen.lumisial_nombre ?? 'Lumisial'} · disponible ${formatCurrency(origen.remanente)}`}
            checked={montos[origen.id] !== undefined} onChange={checked => setMontos(actual => {
              if (checked) return { ...actual, [origen.id]: String(origen.remanente) };
              return Object.fromEntries(Object.entries(actual).filter(([id]) => id !== origen.id));
            })} />
          {montos[origen.id] !== undefined && <TextInput id={`remision-${origen.id}`} label="Importe de esta recepción"
            type="number" value={montos[origen.id]} onChange={valor => setMontos(actual => ({ ...actual, [origen.id]: valor }))}
            min={0.01} max={origen.remanente} step={0.01} required />}
        </div>)}
        <p className="font-semibold">Total remitido: {formatCurrency(Number.isFinite(total) ? total : 0)}</p>
        <DatePicker label="Fecha de la transferencia" value={fecha} onChange={setFecha} max={hoyEnvioChile()} required />
        <MedioPagoFolio comprobante={comprobante} deshabilitado={bloqueado} />
        <Textarea id="remision-descripcion" label="Referencia o descripción" value={descripcion}
          onChange={e => setDescripcion(e.target.value)} />
        <Checkbox label="Confirmo que esta transferencia a JAN ya se realizó." checked={transferido} onChange={setTransferido} />
      </fieldset>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" disabled={bloqueado} onClick={onClose}>Cancelar</Button>
        <Button type="submit" isLoading={operacion.procesando || preparando}
          disabled={!autorizado || cargando || !!errorCarga || operacion.completado || comprobante.subiendo}>
          {operacion.incierto ? 'Verificar remisión' : 'Registrar remisión'}
        </Button>
      </div>
    </form>
  </DialogShell>;
}
